import { useEffect, useMemo, useRef } from "react";
import { GeoJSON, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import type { GeoJsonObject } from "geojson";
import FloraIQMap from "@/components/shared/FloraIQMap";
import type { AreaPiloto } from "@/types/territorial";
import type { QuadraComAnalise, TreeMapPoint } from "@/types/cta-mapa-ui";
import { nivelColor, riscoColor, SBV_CENTER } from "@/config/cta-mapa-config";

interface PilotMapPanelProps {
  area: AreaPiloto;
  quadras: QuadraComAnalise[];
  trees: TreeMapPoint[];
  activeLayers: Set<string>;
  selectedTree: TreeMapPoint | null;
  onSelectTree: (tree: TreeMapPoint) => void;
}

function treeIcon(risco: string, selected: boolean): L.DivIcon {
  const size = selected ? 16 : 12;
  const border = selected ? "3px solid #3b82f6" : "2px solid white";
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;background:${riscoColor(risco)};border:${border};border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.35)"></div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function FitArea({ geom }: { geom: GeoJsonObject | null }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !geom) return;
    const b = L.geoJSON(geom).getBounds();
    if (b.isValid()) {
      map.fitBounds(b, { padding: [20, 20] });
      done.current = true;
    }
  }, [geom, map]);
  return null;
}

export default function PilotMapPanel({ area, quadras, trees, activeLayers, selectedTree, onSelectTree }: PilotMapPanelProps) {
  const areaGeom = (area.geom as GeoJsonObject | null) ?? null;

  const quadrasGeo = useMemo<GeoJsonObject | null>(() => {
    const features = quadras
      .filter((q) => q.quadra.geom)
      .map((q) => ({
        type: "Feature" as const,
        geometry: q.quadra.geom,
        properties: { code: q.quadra.code, heat: q.analise?.heatIslandRisk ?? null, analisada: !!q.analise },
      }));
    return features.length ? ({ type: "FeatureCollection", features } as unknown as GeoJsonObject) : null;
  }, [quadras]);

  const fita = activeLayers.has("fita");

  return (
    <div className="relative w-full h-full overflow-hidden bg-background-100 rounded-card border border-background-200/70">
      <FloraIQMap center={SBV_CENTER} zoom={15} defaultBaseLayer="hybrid" showZoomControl style={{ height: "100%", width: "100%" }}>
        <FitArea geom={areaGeom} />

        {areaGeom && activeLayers.has("perimetro") && (
          <GeoJSON
            key={`perimetro-${area.id}`}
            data={areaGeom}
            style={{ color: "#1B6E4F", weight: 2.5, dashArray: "6 4", fillOpacity: 0.03 }}
            interactive={false}
          />
        )}

        {quadrasGeo && (activeLayers.has("quadras") || fita) && (
          <GeoJSON
            key={`quadras-${quadras.length}-${fita ? "fita" : "plain"}-${activeLayers.has("quadras")}`}
            data={quadrasGeo}
            style={(feature) => {
              const p = (feature?.properties ?? {}) as { heat?: string | null; analisada?: boolean };
              if (fita && p.analisada) {
                return { color: "#2563eb", weight: 1, fillColor: nivelColor(p.heat), fillOpacity: 0.45 };
              }
              return { color: "#2563eb", weight: activeLayers.has("quadras") ? 1 : 0, fillOpacity: 0.03 };
            }}
            onEachFeature={(feature, layer) => {
              const p = (feature.properties ?? {}) as { code?: string; heat?: string | null; analisada?: boolean };
              const extra = p.analisada ? ` — ilha de calor: ${p.heat ? p.heat.toLowerCase() : "não informada"}` : " — sem análise FITA";
              if (p.code) layer.bindTooltip(`Quadra ${p.code}${extra}`, { sticky: true });
            }}
          />
        )}

        {activeLayers.has("arvores") &&
          trees.map((t) => (
            <Marker
              key={t.id}
              position={[t.latitude, t.longitude]}
              icon={treeIcon(t.classeRisco, selectedTree?.id === t.id)}
              eventHandlers={{ click: () => onSelectTree(t) }}
            />
          ))}
      </FloraIQMap>

      <div className="absolute bottom-2 left-2 z-[1000] bg-white/95 backdrop-blur-sm border border-background-200/70 rounded-lg p-2.5 shadow-sm max-w-[200px]">
        <p className="text-[9px] font-semibold text-foreground-700 mb-1.5 font-heading">Legenda</p>
        <div className="space-y-1">
          {[
            ["BAIXO", "Risco baixo"],
            ["MEDIO", "Risco médio"],
            ["ALTO", "Risco alto"],
            ["CRITICO", "Risco crítico"],
            ["", "Sem avaliação de risco"],
          ].map(([code, label]) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: riscoColor(code) }}></div>
              <span className="text-[9px] text-foreground-600">{label}</span>
            </div>
          ))}
          {fita && (
            <p className="text-[9px] text-foreground-500 pt-1 border-t border-background-200/70">
              Preenchimento das quadras: ilha de calor estimada (FITA, preliminar).
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
